import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { ActuatorStateI, AppliancesStateI, DetectorStateI } from '../redux/types/app';
import { DeviceI } from '../redux/types/controllers';
import { formatEntityId, formatOptionalEntityId } from './FormatEntityId';

export const FLOORPLAN_EXPORT_TARGET_ID = 'floorplan-export-target';

const formatPrice = (price?: number): string => (price ? `${price.toFixed(2)}` : '-');

const sumPrices = (prices: (number | undefined)[]): number =>
    prices.reduce((total: number, price) => total + (price || 0), 0);

export interface ExportToPdfLabelsI {
    title: string;
    controllersHeading: string;
    actuatorsHeading: string;
    detectorsHeading: string;
    appliancesHeading: string;
    id: string;
    type: string;
    name: string;
    price: string;
    total: string;
    noItems: string;
    floorplanHeading: string;
}

const captureFloorplanImage = async (): Promise<{ dataUrl: string; width: number; height: number } | null> => {
    const floorplanElement = document.getElementById(FLOORPLAN_EXPORT_TARGET_ID);
    if (!floorplanElement) {
        return null;
    }

    const canvas = await html2canvas(floorplanElement, { backgroundColor: '#ffffff', useCORS: true, scale: 2 });
    return { dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height };
};

export const exportConfigurationToPdf = async (
    controllers: DeviceI[],
    actuators: ActuatorStateI[],
    detectors: DetectorStateI[],
    appliances: AppliancesStateI[],
    labels: ExportToPdfLabelsI
): Promise<void> => {
    const doc = new jsPDF();
    let cursorY = 14;

    doc.setFontSize(16);
    doc.text(labels.title, 14, cursorY);
    cursorY += 10;

    doc.setFontSize(12);
    doc.text(labels.controllersHeading, 14, cursorY);
    cursorY += 4;

    if (controllers.length === 0) {
        cursorY += 6;
        doc.setFontSize(10);
        doc.text(labels.noItems, 14, cursorY);
        cursorY += 8;
    } else {
        autoTable(doc, {
            startY: cursorY + 2,
            head: [[labels.id, labels.type, labels.name, labels.price]],
            body: controllers.map((controller) => [
                formatEntityId('controller', controller.id),
                controller.type,
                `${controller.name}${controller.colorShortcut}`,
                formatPrice(controller.price)
            ]),
            foot: [['', '', labels.total, formatPrice(sumPrices(controllers.map((controller) => controller.price)))]],
            theme: 'grid',
            styles: { fontSize: 9 },
            headStyles: { fillColor: [0, 145, 234] }
        });
        cursorY = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
    }

    doc.setFontSize(12);
    doc.text(labels.detectorsHeading, 14, cursorY);
    cursorY += 4;

    if (detectors.length === 0) {
        cursorY += 6;
        doc.setFontSize(10);
        doc.text(labels.noItems, 14, cursorY);
        cursorY += 8;
    } else {
        autoTable(doc, {
            startY: cursorY + 2,
            head: [[labels.id, labels.type, labels.name, labels.price]],
            body: detectors.map((detector) => [
                formatOptionalEntityId('detector', detector.id),
                detector.type,
                detector.name,
                formatPrice(detector.price)
            ]),
            foot: [['', '', labels.total, formatPrice(sumPrices(detectors.map((detector) => detector.price)))]],
            theme: 'grid',
            styles: { fontSize: 9 },
            headStyles: { fillColor: [0, 145, 234] }
        });
        cursorY = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
    }

    doc.setFontSize(12);
    doc.text(labels.actuatorsHeading, 14, cursorY);
    cursorY += 4;

    if (actuators.length === 0) {
        cursorY += 6;
        doc.setFontSize(10);
        doc.text(labels.noItems, 14, cursorY);
        cursorY += 8;
    } else {
        autoTable(doc, {
            startY: cursorY + 2,
            head: [[labels.id, labels.type, labels.name, labels.price]],
            body: actuators.map((actuator) => [
                formatOptionalEntityId('actuator', actuator.id),
                actuator.type,
                actuator.name,
                formatPrice(actuator.price)
            ]),
            foot: [['', '', labels.total, formatPrice(sumPrices(actuators.map((actuator) => actuator.price)))]],
            theme: 'grid',
            styles: { fontSize: 9 },
            headStyles: { fillColor: [0, 145, 234] }
        });
        cursorY = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
    }

    doc.setFontSize(12);
    doc.text(labels.appliancesHeading, 14, cursorY);
    cursorY += 4;

    if (appliances.length === 0) {
        cursorY += 6;
        doc.setFontSize(10);
        doc.text(labels.noItems, 14, cursorY);
    } else {
        autoTable(doc, {
            startY: cursorY + 2,
            head: [[labels.id, labels.name]],
            body: appliances.map((appliance) => [
                formatEntityId('appliance', appliance.id),
                appliance.label
            ]),
            theme: 'grid',
            styles: { fontSize: 9 },
            headStyles: { fillColor: [150, 150, 150] }
        });
    }

    const floorplanImage = await captureFloorplanImage();
    if (floorplanImage) {
        doc.addPage();
        doc.setFontSize(16);
        doc.text(labels.floorplanHeading, 14, 14);

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const availableWidth = pageWidth - 28;
        const availableHeight = pageHeight - 28;
        const scale = Math.min(availableWidth / floorplanImage.width, availableHeight / floorplanImage.height);
        const imageWidth = floorplanImage.width * scale;
        const imageHeight = floorplanImage.height * scale;

        doc.addImage(floorplanImage.dataUrl, 'PNG', 14, 24, imageWidth, imageHeight);
    }

    doc.save('configuration.pdf');
};
